export const ISO_COUNTRY_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS',
  'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
  'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE',
  'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF',
  'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
  'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM',
  'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC',
  'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
  'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA',
  'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG',
  'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
  'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO',
  'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
  'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW'
] as const;

export type IsoCountryCode = typeof ISO_COUNTRY_CODES[number];

export const COUNTRY_CODE_ALIASES: Record<string, IsoCountryCode> = {
  bolivia: 'BO',
  brunei: 'BN',
  'czech republic': 'CZ',
  'democratic republic of congo': 'CD',
  'dr congo': 'CD',
  'ivory coast': 'CI',
  iran: 'IR',
  laos: 'LA',
  moldova: 'MD',
  'north korea': 'KP',
  palestine: 'PS',
  'republic of congo': 'CG',
  russia: 'RU',
  'south korea': 'KR',
  syria: 'SY',
  taiwan: 'TW',
  tanzania: 'TZ',
  turkey: 'TR',
  uk: 'GB',
  'great britain': 'GB',
  england: 'GB',
  scotland: 'GB',
  wales: 'GB',
  usa: 'US',
  'united states of america': 'US',
  venezuela: 'VE',
  vietnam: 'VN'
};

export function countryFlag(countryCode: string): string {
  const normalizedCode = countryCode.trim().toUpperCase();
  if (!normalizedCode.match(/^[A-Z]{2}$/)) return '';

  return [...normalizedCode].map(character => String.fromCodePoint(127397 + character.charCodeAt(0))).join('');
}

export function countryFlagImageUrl(countryCode: string): string {
  const normalizedCode = countryCode.trim().toLowerCase();
  if (!normalizedCode.match(/^[a-z]{2}$/)) return '';

  return `https://flagcdn.com/w40/${normalizedCode}.png`;
}

export function countryDisplayName(countryCode: string, locale = 'en'): string {
  const normalizedCode = countryCode.trim().toUpperCase();
  if (!normalizedCode.match(/^[A-Z]{2}$/)) return countryCode;

  return new Intl.DisplayNames([locale, 'en'], { type: 'region' }).of(normalizedCode) ?? normalizedCode;
}

export function normalizeCountryName(country: string): string {
  return country
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\b(the|republic of|kingdom of|state of|states of)\b/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

export function buildCountryCodeByDisplayName(locale = 'en'): Record<string, IsoCountryCode> {
  const englishRegionNames = new Intl.DisplayNames(['en'], { type: 'region' });
  const localizedRegionNames = new Intl.DisplayNames([locale, 'en'], { type: 'region' });
  const countryCodesByName: Record<string, IsoCountryCode> = { ...COUNTRY_CODE_ALIASES };

  ISO_COUNTRY_CODES.forEach(countryCode => {
    [englishRegionNames.of(countryCode), localizedRegionNames.of(countryCode), countryCode]
      .filter((name): name is string => Boolean(name))
      .forEach(name => {
        countryCodesByName[normalizeCountryName(name)] = countryCode;
      });
  });

  return countryCodesByName;
}

export function countryCodeFromText(country: string, locale = 'en'): IsoCountryCode | '' {
  const normalizedCountry = country.trim().toLowerCase();
  const directIsoCode = normalizedCountry.match(/^[a-z]{2}$/) ? normalizedCountry.toUpperCase() : '';
  if (ISO_COUNTRY_CODES.includes(directIsoCode as IsoCountryCode)) return directIsoCode as IsoCountryCode;

  return buildCountryCodeByDisplayName(locale)[normalizeCountryName(country)] ?? '';
}